import React, { useEffect, useState } from 'react';

import WidgetsCheckout from '@src/components/Checkout/Checkout';
import Container from '@src/components/reusable/Container';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { buyerSelector, setupAuthOngoingSelector } from '@src/features/customers';
import { completeCartSelector } from '@src/features/cart/store/cart';
import { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import { LayoutColors } from '@src/core/theme';
import styled from 'styled-components';
import { currentOrderSelector, resumeDirectLinkOrder } from '@src/features/orders';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import SpinnerBar from '@src/components/reusable/spinner/SpinnerBar';
import { LoadingWrapper } from '../Base/styles';

const LINK_ORDER_LOOKUP_ATTEMPTS = 3;
const LINK_ORDER_LOOKUP_RETRY_DELAY_MS = 1000;

export const Checkout = () => {
  const { i18n } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [_, logged] = useAppSelector(buyerSelector);
  const { hasErrors: hasCartErrors, cartErrors, cartItemErrors } = useAppSelector(completeCartSelector);
  const currentOrder = useAppSelector(currentOrderSelector);
  const setupAuthOngoing = useAppSelector(setupAuthOngoingSelector);
  const [linkOrderLookedUp, setLinkOrderLookedUp] = useState(false);
  const { areFeatureFlagsReady, isFeatureFlagEnabled } = useFeatureFlag();
  // The new checkout signs the buyer in on its own; only the legacy one needs the process page first.
  const signsInOnItsOwn = isFeatureFlagEnabled('PR-3043-checkout');

  useEffect(() => {
    if (areFeatureFlagsReady && !signsInOnItsOwn && !logged) {
      navigate('../process');
    }
  }, [areFeatureFlagsReady, signsInOnItsOwn]);

  // A checked-out cart holds its own seats, so reloading the link revalidates it into availability
  // errors. Those only stand when the buyer has no order from this link; the checkout would look the
  // order up itself, but it never mounts behind the errors, so the page looks it up first.
  const mustLookUpLinkOrder = hasCartErrors && !currentOrder;

  useEffect(() => {
    if (!mustLookUpLinkOrder || !logged || linkOrderLookedUp) {
      return undefined;
    }

    let active = true;

    // A failed lookup is not an answer: settling on the cart errors after one would strand a buyer
    // whose order exists, so it is retried before they stand.
    const lookUpLinkOrder = async () => {
      for (let attempt = 1; active; attempt++) {
        try {
          await dispatch(resumeDirectLinkOrder());
          return;
        } catch (error) {
          if (attempt >= LINK_ORDER_LOOKUP_ATTEMPTS) {
            console.error('Could not look up the direct link order:', error);
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, LINK_ORDER_LOOKUP_RETRY_DELAY_MS));
        }
      }
    };

    lookUpLinkOrder().finally(() => {
      if (active) {
        setLinkOrderLookedUp(true);
      }
    });

    return () => {
      active = false;
    };
  }, [mustLookUpLinkOrder, logged, linkOrderLookedUp, dispatch]);

  if (mustLookUpLinkOrder && (setupAuthOngoing || (logged && !linkOrderLookedUp))) {
    return (
      <LoadingWrapper>
        <SpinnerBar />
      </LoadingWrapper>
    );
  }

  if (mustLookUpLinkOrder) {
    return (
      <Container>
        <ErrorMessage>
          {cartErrors?.map((error) => (
            <span key={`cart-error-${error.type}`}>
              {i18n(`direct_link_checkout_error_${error.type}` as DefaultMessagesKeys)}
            </span>
          ))}
          {cartItemErrors?.map((error, index) => {
            return error?.map((item) => (
              <span key={`cart-item-error-${index}-${item.type}`}>
                {i18n(`direct_link_checkout_error_${item.type}` as DefaultMessagesKeys)}
              </span>
            ));
          })}
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <WidgetsCheckout editable={false} skipResumeCart={true} />
    </Container>
  );
};

export default Checkout;

const ErrorMessage = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${LayoutColors.errorDark} !important;

  span {
    text-align: center;
  }
`;
